import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./FoodGame.css";

import rotiImg from "../assets/food/roti.png";
import biriyaniImg from "../assets/food/biriyani.png";
import hoppersImg from "../assets/food/hoppers.png";
import stringHoppersImg from "../assets/food/stringhoppers.png";
import shorteatsImg from "../assets/food/shorteats.png";

// all the dishes available to puzzle, each one has a title, image and list of facts
const DISHES = [
  {
    id: "roti",
    title: "Roti, Lentil Curry & Mutton Curry",
    image: rotiImg,
    facts: [
      "Roti is a soft, delicious flatbread.",
      "Lentil curry is called 'parippu' in Sri Lanka.",
    ],
  },
  {
    id: "biriyani",
    title: "Biriyani, Chicken Leg & Boiled Egg",
    image: biriyaniImg,
    facts: [
      "Biriyani is a colourful rice dish with lots of spices!",
      "It is normally a main dish for celebrations.",
    ],
  },
  {
    id: "appam",
    title: "Appam, Sambol & Sothi",
    image: hoppersImg,
    facts: [
      "Appam is also known as 'hoppers'.",
      "It has a super soft middle and crispy edges.",
      "Sambol adds spice, and sothi is a coconut curry.",
    ],
  },
  {
    id: "string-hoppers",
    title: "String Hoppers, Fish Curry & Sothi",
    image: stringHoppersImg,
    facts: [
      "String hoppers look like noodles, but they are made from rice flour!",
      "They are steamed, and served with curry.",
    ],
  },
  {
    id: "shorteats",
    title: "Shorteats: Mixture, Vadai & Murukku",
    image: shorteatsImg,
    facts: [
      "Shorteats are Sri Lankan snacks.",
      "They can either be sweet or spicy!"
    ],
  },
];

// each difficulty defines how many rows and columns the puzzle grid has
const DIFFICULTIES = {
  easy:   { label: "Easy",   rows: 2, cols: 3 },
  medium: { label: "Medium", rows: 3, cols: 3 },
  hard:   { label: "Hard",   rows: 3, cols: 4 },
};

// fisher-yates shuffle, returns a new array and leaves the orignal untouched
function shuffleArray(array) {
  const copied = [...array];
  for (let i = copied.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copied[i], copied[j]] = [copied[j], copied[i]];
  }
  return copied;
}

// builds a list of piece objects for the given grid size, then shuffles them
// each piece knows its correct position so we can check if the puzzle is solved
function createPuzzlePieces(rows, cols) {
  const pieces = [];
  let index = 0;

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      pieces.push({
        id: `piece-${row}-${col}`,
        correctIndex: index,
        row,
        col,
      });
      index += 1;
    }
  }

  return shuffleArray(pieces);
}

export default function FoodGame({ onBackHome }) {
  const navigate = useNavigate();

  // which screen is showing, either the dish slection screen or the active puzzle
  const [screen, setScreen] = useState("select"); // select | puzzle

  // index into DISHES for whichever dish is currently being puzzled
  const [selectedDishIndex, setSelectedDishIndex] = useState(null);

  // which difficulty key is currently selected
  const [difficultyKey, setDifficultyKey] = useState("easy");

  // the shuffled array of piece objects for the current puzzle
  const [pieces, setPieces] = useState([]);

  // index of the piece being dragged, null when no drag is in progress
  const [draggedIndex, setDraggedIndex] = useState(null);

  // index of the first piece tapped in a tap-to-swap interaction
  const [selectedSwapIndex, setSelectedSwapIndex] = useState(null);

  // controls wether the completed puzzle modal is visible
  const [showModal, setShowModal] = useState(false);

  // triggers the board pulse animation when the puzzle is finished
  const [celebrationActive, setCelebrationActive] = useState(false);

  // controls wether the difficulty dropdown is open on the puzzle screen
  const [showDifficultyMenu, setShowDifficultyMenu] = useState(false);

  // controls wether the hint modal is open
  const [showHint, setShowHint] = useState(false);

  const currentDish = selectedDishIndex !== null ? DISHES[selectedDishIndex] : null;
  const difficulty = DIFFICULTIES[difficultyKey];

  // the puzzle is complete when every piece is sitting in its correct position
  const isComplete = useMemo(() => {
    if (!pieces.length) return false;
    return pieces.every((piece, index) => piece.correctIndex === index);
  }, [pieces]);

  // when the puzzle becomes complete, trigger the celebraton then open the modal
  useEffect(() => {
    if (isComplete && screen === "puzzle") {
      setCelebrationActive(true);
      const timer = setTimeout(() => {
        setShowModal(true);
        setCelebrationActive(false);
      }, 4100);
      return () => clearTimeout(timer);
    }
  }, [isComplete, screen]);

  // sets up a new puzzle for the given dish and difficulty
  const startPuzzle = (dishIndex, chosenDifficulty = difficultyKey) => {
    const level = DIFFICULTIES[chosenDifficulty];
    const generatedPieces = createPuzzlePieces(level.rows, level.cols);

    setSelectedDishIndex(dishIndex);
    setDifficultyKey(chosenDifficulty);
    setPieces(generatedPieces);
    setDraggedIndex(null);
    setSelectedSwapIndex(null);
    setShowModal(false);
    setCelebrationActive(false);
    setScreen("puzzle");
  };

  // goes back to the dish selection screen and resets everything
  const goHome = () => {
    setScreen("select");
    setSelectedDishIndex(null);
    setPieces([]);
    setDraggedIndex(null);
    setSelectedSwapIndex(null);
    setShowModal(false);
    setCelebrationActive(false);

    // call the parent callback if one was passed in as a prop
    if (onBackHome) onBackHome();
  };

  // reshuffles the current puzzle without changing the dish or difficulty
  const restartPuzzle = () => {
    if (selectedDishIndex === null) return;
    startPuzzle(selectedDishIndex, difficultyKey);
  };

  // moves to the next dish in the DISHES array, wrapping around at the end
  const goToNextPuzzle = () => {
    if (selectedDishIndex === null) return;
    const nextIndex = (selectedDishIndex + 1) % DISHES.length;
    startPuzzle(nextIndex, difficultyKey);
  };

  // swaps two pieces in the pieces array by their index positions
  const swapPieces = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    setPieces((prev) => {
      const updated = [...prev];
      [updated[fromIndex], updated[toIndex]] = [updated[toIndex], updated[fromIndex]];
      return updated;
    });
  };

  // drag-and-drop handlers below, these work alongside the tap-to-swap system
  const handleDragStart = (index) => {
    if (showModal) return;
    setDraggedIndex(index);
  };

  const handleDrop = (index) => {
    if (showModal) return;
    if (draggedIndex === null) return;
    swapPieces(draggedIndex, index);
    setDraggedIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // tap-to-swap handler, first tap selects a piece and second tap swaps them
  const handlePieceClick = (index) => {
    if (showModal) return;

    if (selectedSwapIndex === null) {
      setSelectedSwapIndex(index);
      return;
    }

    // tapping the same piece again deselcts it
    if (selectedSwapIndex === index) {
      setSelectedSwapIndex(null);
      return;
    }

    swapPieces(selectedSwapIndex, index);
    setSelectedSwapIndex(null);
  };

  return (
    <div className="foodGame">
      {/* decorative glow blobs in the background */}
      <div className="foodGame__glow foodGame__glow--left" />
      <div className="foodGame__glow foodGame__glow--right" />

      {/* dish selection screen */}
      {screen === "select" && (
        <section className="foodSelect">
          <div className="puzzleHomeCorner">
            <button
              className="homeBtn"
              onClick={() => navigate("/loading", { state: { to: "/home" } })}
              aria-label="Go Home"
              title="Go Home"
              type="button"
            >
              {/* inline svg so the fill colour is easy to overide in css */}
              <svg
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="homeBtn__icon"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 0L0 6V8H1V15H4V10H7V15H15V8H16V6L14 4.5V1H11V2.25L8 0ZM9 10H12V13H9V10Z"
                  fill="#ffffff"
                />
              </svg>
            </button>
          </div>

          <div className="foodSelect__header">
            <h1>Food Puzzle Game</h1>
            <p>Pick a yummy Sri Lankan dish, then solve the picture puzzle.</p>
          </div>

          {/* difficulty selector shown above the dish grid */}
          <div className="difficultyBar">
            {Object.entries(DIFFICULTIES).map(([key, value]) => (
              <button
                key={key}
                className={`difficultyBar__button ${difficultyKey === key ? "is-active" : ""}`}
                onClick={() => setDifficultyKey(key)}
                type="button"
              >
                {value.label} ({value.rows}×{value.cols})
              </button>
            ))}
          </div>

          {/* grid of dish cards, clicking one starts the puzzle */}
          <div className="foodGrid">
            {DISHES.map((dish, index) => (
              <button
                key={dish.id}
                className="foodCard"
                type="button"
                onClick={() => startPuzzle(index, difficultyKey)}
              >
                <div className="foodCard__imageWrap">
                  <img src={dish.image} alt={dish.title} className="foodCard__image" />
                </div>
                <h2>{dish.title}</h2>
                <span className="foodCard__cta">Play Puzzle</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* active puzzle screen */}
      {screen === "puzzle" && currentDish && (
        <section className="puzzleScreen">
          <div className="puzzleTopBar">
            <div className="puzzleTopBar__middle">
              <h2>{currentDish.title}</h2>
              <p>
                Difficulty: {difficulty.label} • {difficulty.rows}×{difficulty.cols}
              </p>
            </div>

            <div className="puzzleTopBar__actions">
              <button
                className="gameButton gameButton--secondary"
                onClick={goHome}
                type="button"
              >
                ← Change Puzzle
              </button>

              {/* difficulty dropdown sits relative to this wrapper */}
              <div className="puzzleTopBar__difficultyWrap">
                <button
                  className="gameButton gameButton--secondary"
                  onClick={() => setShowDifficultyMenu((v) => !v)}
                  type="button"
                >
                  Change Difficulty: {difficulty.label}
                </button>
                {showDifficultyMenu && (
                  <div className="difficultyDropdown">
                    {Object.entries(DIFFICULTIES).map(([key, value]) => (
                      <button
                        key={key}
                        type="button"
                        className={`difficultyDropdown__option ${difficultyKey === key ? "is-active" : ""}`}
                        onClick={() => {
                          startPuzzle(selectedDishIndex, key);
                          setShowDifficultyMenu(false);
                        }}
                      >
                        {value.label} ({value.rows}×{value.cols})
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button className="gameButton gameButton--hint" onClick={() => setShowHint(true)} type="button">
                Hint
              </button>

              <button className="gameButton" onClick={restartPuzzle} type="button">
                Shuffle Again
              </button>
            </div>
          </div>

          {/* instrution text below the top bar */}
          <div className="puzzleHint">
            <p>
              Drag the puzzle pieces to swap them, or tap one piece and then another piece to swap.
            </p>
          </div>

          {/* the puzzle board, is-complete class triggers the pulse animaton */}
          <div className={`puzzleBoardWrap${celebrationActive ? " is-complete" : ""}`}>
            <div
              className={`puzzleBoard puzzleBoard--${difficultyKey}`}
              style={{
                gridTemplateColumns: `repeat(${difficulty.cols}, 1fr)`,
                gridTemplateRows: `repeat(${difficulty.rows}, 1fr)`,
              }}
            >
              {pieces.map((piece, index) => {
                // calculate background-position percentage for this piece
                const xPercent =
                  difficulty.cols === 1 ? 0 : (piece.col / (difficulty.cols - 1)) * 100;
                const yPercent =
                  difficulty.rows === 1 ? 0 : (piece.row / (difficulty.rows - 1)) * 100;

                return (
                  <button
                    key={`${piece.id}-${index}`}
                    type="button"
                    className={`puzzlePiece ${
                      selectedSwapIndex === index ? "is-selected" : ""
                    }`}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleDrop(index)}
                    onDragEnd={handleDragEnd}
                    onClick={() => handlePieceClick(index)}
                    aria-label={`Puzzle piece ${index + 1}`}
                  >
                    <div
                      className="puzzlePiece__image"
                      style={{
                        backgroundImage: `url(${currentDish.image})`,
                        backgroundSize: `${difficulty.cols * 100}% ${difficulty.rows * 100}%`,
                        backgroundPosition: `${xPercent}% ${yPercent}%`,
                      }}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          {/* hint modal shows the full image so the player knows what they are building */}
          {showHint && (
            <div className="foodModalOverlay" onClick={() => setShowHint(false)}>
              <div className="hintModal" onClick={(e) => e.stopPropagation()}>
                <h3>Here's your hint!</h3>
                <img
                  src={currentDish.image}
                  alt={currentDish.title}
                  className="hintModal__image"
                />
                <button className="gameButton" onClick={() => setShowHint(false)} type="button">
                  Close & Continue
                </button>
              </div>
            </div>
          )}

          {/* completion modal shows the dish facts after the puzzle is solved */}
          {showModal && (
            <div className="foodModalOverlay">
              <div className="foodModal">
                <h3>Great job!</h3>
                <h4>{currentDish.title}</h4>

                <div className="foodModal__content">
                  <ul>
                    {currentDish.facts.map((fact, index) => (
                      <li key={index}>{fact}</li>
                    ))}
                  </ul>

                  <img
                    src={currentDish.image}
                    alt={currentDish.title}
                    className="foodModal__image"
                  />
                </div>

                <div className="foodModal__buttons">
                  <button className="gameButton" onClick={goToNextPuzzle} type="button">
                    Next Puzzle
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}